import { useCallback, useState } from 'react';

import { emailIsValid } from '@/components/signup/utils/email-is-valid';

import {
	type AddEmailResult,
	EmailInviteValidationError,
	MAX_ALLOWED_EMAILS,
	type UseEmailInvitesInput,
	type UseEmailInvitesInputOptions,
} from './use-email-invites-input.types.ts';

const isDuplicate = (emails: string[], candidate: string): boolean => {
	const key = candidate.toLowerCase();
	return emails.some((email) => email.toLowerCase() === key);
};

const ALLOWED_CHARS_STRICT = /^[A-Za-z0-9._%+-]+$/;

const hasAllowedLocalPart = (candidate: string): boolean => {
	const localPart = candidate.split('@')[0];
	return !!localPart && ALLOWED_CHARS_STRICT.test(localPart);
};

export const isValidInviteEmail = (candidate: string): boolean =>
	emailIsValid(candidate) && hasAllowedLocalPart(candidate);

/**
 * Owns email input state and validation for the onboarding-wizard invite step.
 *
 * Do NOT use outside of fg: spice-refactor-jira-onboarding-wizard-invites, contact #help-spice for details
 */
export const useEmailInvitesInput = (
	options?: UseEmailInvitesInputOptions,
): UseEmailInvitesInput => {
	const { maxAllowedEmails: maxAllowedEmailsOption } = options ?? {};
	const maxAllowedEmails =
		typeof maxAllowedEmailsOption === 'number' &&
		Number.isInteger(maxAllowedEmailsOption) &&
		maxAllowedEmailsOption > 0
			? maxAllowedEmailsOption
			: MAX_ALLOWED_EMAILS;

	const [validatedEmails, setValidatedEmails] = useState<string[]>([]);

	const addEmail = useCallback(
		(email: string): AddEmailResult => {
			const candidate = email.trim();
			const current = validatedEmails;

			if (current.length >= maxAllowedEmails) {
				return { ok: false, error: EmailInviteValidationError.MAX_ALLOWED_EXCEEDED };
			}

			if (!isValidInviteEmail(candidate)) {
				return { ok: false, error: EmailInviteValidationError.INVALID_FORMAT };
			}

			if (isDuplicate(current, candidate)) {
				return { ok: false, error: EmailInviteValidationError.DUPLICATE };
			}

			setValidatedEmails([...current, candidate]);

			return { ok: true };
		},
		[maxAllowedEmails, validatedEmails],
	);

	const removeEmail = useCallback((email: string) => {
		const key = email.toLowerCase();
		setValidatedEmails((current) => current.filter((existing) => existing.toLowerCase() !== key));
	}, []);

	const reset = useCallback(() => {
		setValidatedEmails([]);
	}, []);

	const getEmails = useCallback(() => [...validatedEmails], [validatedEmails]);

	return { validatedEmails, getEmails, addEmail, removeEmail, reset };
};
